import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { PlusCircle, AlertTriangle, ArrowDownUp, Eye } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectIsAuthenticated, selectUser } from '@/store/slices/authSlice';
import {
  initializeUserSpecificData,
  reviewsSelectors,
  selectReviewsLoading,
  selectUserSpecificError,
} from '@/store/slices/userSpecificSlice';
import type { ReduxReview } from '@/store/slices/userSpecificSlice';
import { selectDrinkById, selectAllDrinks } from '@/store/slices/globalDataSlice';
import type { Drink as FirestoreDrink } from '@/types/firestore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import DiaryFilterControls from '@/components/diary/DiaryFilterControls';
import {
  selectAllDiaryFilters,
  diaryFiltersInitialState,
  selectDiarySortField,
  selectDiarySortDirection,
  setSortField as setDiarySortField,
  toggleSortDirection as toggleDiarySortDirection
} from '@/store/slices/diaryFiltersSlice';
import type { DiarySortField } from '@/store/slices/diaryFiltersSlice';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import DiaryEntryPage from '../components/diary/DiaryEntry';
import { selectLocationEntities } from '@/store/slices/locationsSlice';
import StarRating from '@/components/ui/star-rating';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Interface for items used in DiaryReviewItem props
// Extends ReduxReview structure with additional properties
interface DisplayReview extends Omit<ReduxReview, 'reviewText'> {
    // Fields from FirestoreReview via Omit (most are required unless optional in FirestoreReview)
    brandName: string;
    drinkName: string;
    rating: number;
    reviewText?: string;  // Optional in FirestoreReview
    isPublic: boolean;
    brandId: string;
    locationId?: string;  // Optional in FirestoreReview
    locationName?: string;// Optional in FirestoreReview
    username: string; // Also on FirestoreReview
    userId: string;   // Also on FirestoreReview
    // Adding flavors and attributes properties
    flavors?: FlavorAttributeItem[];
    attributes?: FlavorAttributeItem[];
}

interface FlavorAttributeItem { 
  id: string;
  name: string;
  groupId?: number;
}

// Define SORTABLE_FIELDS constant
const SORTABLE_FIELDS: { value: DiarySortField; label: string }[] = [
    { value: 'date', label: 'Date' },
    { value: 'rating', label: 'Rating' },
    { value: 'drinkName', label: 'Drink Name' },
    { value: 'brandName', label: 'Brand Name' },
    { value: 'locationName', label: 'Location Name' },
];

// Modify DiaryReviewItem to be a button that calls onSelectReview
interface DiaryReviewItemProps {
  review: DisplayReview;
  onSelectReview: (reviewId: string) => void;
}

const DiaryReviewItem: React.FC<DiaryReviewItemProps> = ({ review, onSelectReview }) => {
  const drink = useAppSelector(state => selectDrinkById(state, review.drinkId)) as FirestoreDrink | undefined;
  const flavors = review.flavors || drink?.flavors;
  const attributes = review.attributes || drink?.attributes;

  return (
    <button 
      key={review.id} 
      onClick={() => onSelectReview(review.id)}
      className="block group text-left w-full h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
    >
      <Card className="h-full flex flex-col overflow-hidden gap-2 shadow-sm group-hover:shadow-xl transition-shadow duration-200 group-focus-visible:ring-2 group-focus-visible:ring-primary">
        <CardHeader className="pb-0">
          <div className="flex justify-between items-start gap-2">
            <CardTitle className="text-lg font-semibold text-primary leading-tight group-hover:text-primary/80 flex items-center">
              {/* Accessing potentially optional fields */}
              {review.brandName || 'Unknown Brand'} {review.drinkName || 'Unknown Drink'}
              {review.isPublic && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Eye className="ml-2 h-4 w-4 text-primary" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Public Entry</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </CardTitle>
            <span className="text-xs text-muted-foreground whitespace-nowrap pt-1">
              {new Date(review.date).toLocaleDateString()}
            </span>
          </div>
        </CardHeader>
        <CardContent className="flex-grow flex flex-col space-y-2.5">
          {(flavors?.length || 0) > 0 || (attributes?.length || 0) > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {flavors?.map((flavor: FlavorAttributeItem) => (
                <Badge key={flavor.id} variant="secondary" className="text-xs">{flavor.name}</Badge>
              ))}
              {attributes?.map((attr: FlavorAttributeItem) => (
                <Badge key={attr.id} variant="secondary" className="text-xs">{attr.name}</Badge>
              ))}
            </div>
          ) : null}
          <div className="flex items-center">
            <StarRating value={review.rating ?? 0} readOnly size="sm" />
            <span className="ml-2 text-sm font-medium text-muted-foreground">({(review.rating ?? 0).toFixed(1)})</span>
          </div>
          {review.reviewText ? (
            <p className="text-sm text-card-foreground flex-grow leading-relaxed hyphens-auto">
              {review.reviewText.length > 150 ? `${review.reviewText.substring(0, 147)}...` : review.reviewText}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground italic flex-grow">No review text.</p>
          )}
        </CardContent>
      </Card>
    </button>
  );
};

const DiaryPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const currentUser = useAppSelector(selectUser);
  
  const allReviews = useAppSelector(reviewsSelectors.selectAll) as DisplayReview[];
  const reviewsLoading = useAppSelector(selectReviewsLoading);
  const error = useAppSelector(selectUserSpecificError);
  
  // Select all filters from the diaryFilters slice
  const filters = useAppSelector(selectAllDiaryFilters);
  const sortField = useAppSelector(selectDiarySortField);
  const sortDirection = useAppSelector(selectDiarySortDirection);
  const locationEntities = useAppSelector(selectLocationEntities);
  const allDrinks = useAppSelector(selectAllDrinks);

  // Remove viewMode state and just use activeEntryId to control dialog visibility
  const [activeEntryId, setActiveEntryId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (isAuthenticated && currentUser?.id) {
      if (reviewsLoading !== 'pending' && reviewsLoading !== 'succeeded') {
         dispatch(initializeUserSpecificData(currentUser.id));
      }
    }
  }, [dispatch, isAuthenticated, currentUser?.id, reviewsLoading]);

  const filteredAndSortedReviews = useMemo(() => {
    let processedReviews: DisplayReview[] = [...allReviews];
    
    // Filter logic using DisplayReview type
    if (filters.searchTerm) {
      const lowerSearchTerm = filters.searchTerm.toLowerCase();
      processedReviews = processedReviews.filter((review) => // Type inferred
        review.drinkName.toLowerCase().includes(lowerSearchTerm) ||
        review.brandName.toLowerCase().includes(lowerSearchTerm) ||
        review.reviewText?.toLowerCase().includes(lowerSearchTerm)
      );
    }
    if (filters.isPublic !== 'all') {
      processedReviews = processedReviews.filter((review) => review.isPublic === (filters.isPublic === 'public'));
    }
    if (filters.minRating !== null) {
      processedReviews = processedReviews.filter((review) => review.rating >= filters.minRating!);
    }
    if (filters.maxRating !== null) {
      processedReviews = processedReviews.filter((review) => review.rating <= filters.maxRating!);
    }
    if (filters.selectedBrandIds.length > 0) {
      processedReviews = processedReviews.filter((review) => filters.selectedBrandIds.includes(review.brandId));
    }
    
    // Filter by flavors
    if (filters.selectedFlavorIds.length > 0) {
      processedReviews = processedReviews.filter((review) => {
        // If the review has explicit flavors array, check against that
        if (review.flavors && review.flavors.length > 0) {
          return review.flavors.some(flavor => 
            filters.selectedFlavorIds.includes(flavor.id)
          );
        }
        
        // Otherwise, check against the drink's flavors
        const drink = allDrinks.find(d => d.id === review.drinkId);
        if (drink && drink.flavors && drink.flavors.length > 0) {
          return drink.flavors.some(flavor => 
            filters.selectedFlavorIds.includes(flavor.id)
          );
        }
        
        return false;
      });
    }
    
    // Filter by attributes
    if (filters.selectedAttributeIds.length > 0) {
      processedReviews = processedReviews.filter((review) => {
        // If the review has explicit attributes array, check against that
        if (review.attributes && review.attributes.length > 0) {
          return review.attributes.some(attr => 
            filters.selectedAttributeIds.includes(attr.id)
          );
        }
        
        // Otherwise, check against the drink's attributes
        const drink = allDrinks.find(d => d.id === review.drinkId);
        if (drink && drink.attributes && drink.attributes.length > 0) {
          return drink.attributes.some(attr => 
            filters.selectedAttributeIds.includes(attr.id)
          );
        }
        
        return false;
      });
    }
    
    // Filter by location types
    if (filters.selectedLocationTypes.length > 0) {
      processedReviews = processedReviews.filter((review) => {
        if (!review.locationId) return false;
        
        const location = locationEntities[review.locationId];
        return location && filters.selectedLocationTypes.includes(location.locationType);
      });
    }
    
    // Filter by specific locations
    if (filters.selectedLocationIds.length > 0) {
      processedReviews = processedReviews.filter((review) => 
        review.locationId && filters.selectedLocationIds.includes(review.locationId)
      );
    }
    
    const startDate = filters.dateRange.start ? new Date(filters.dateRange.start) : null;
    const endDate = filters.dateRange.end ? new Date(filters.dateRange.end) : null;
    if (startDate) {
        startDate.setHours(0, 0, 0, 0); 
        processedReviews = processedReviews.filter((review) => review.date >= startDate.getTime());
    }
    if (endDate) {
        endDate.setHours(23, 59, 59, 999); 
        processedReviews = processedReviews.filter((review) => review.date <= endDate.getTime());
    }

    // Sorting logic using DisplayReview type
    processedReviews.sort((a, b) => { // Types inferred
        let compareA: any = null;
        let compareB: any = null;
        switch (filters.sortField) {
            case 'rating': compareA = a.rating; compareB = b.rating; break;
            case 'drinkName': compareA = a.drinkName.toLowerCase(); compareB = b.drinkName.toLowerCase(); break;
            case 'brandName': compareA = a.brandName.toLowerCase(); compareB = b.brandName.toLowerCase(); break;
            case 'locationName': compareA = a.locationName?.toLowerCase(); compareB = b.locationName?.toLowerCase(); break;
            case 'date': default: compareA = a.date; compareB = b.date; break;
        }
        if (compareA == null && compareB == null) return 0;
        if (compareA == null) return filters.sortDirection === 'asc' ? -1 : 1;
        if (compareB == null) return filters.sortDirection === 'asc' ? 1 : -1;
        let comparison = 0;
        if (compareA < compareB) comparison = -1;
        if (compareA > compareB) comparison = 1;
        return filters.sortDirection === 'asc' ? comparison : comparison * -1;
    });

    return processedReviews;
  }, [allReviews, filters, locationEntities, allDrinks]);

  const filtersAreDefault = useMemo(() => {
      return JSON.stringify(filters) === JSON.stringify(diaryFiltersInitialState);
  }, [filters]);

  const handleAddNewEntry = () => {
    setActiveEntryId('new');
  };

  const handleSelectReview = (reviewId: string) => {
    setActiveEntryId(reviewId);
  };

  const handleCloseEntryPage = () => {
    setActiveEntryId(undefined);
  };

  // Adjust top padding for sticky positioning based on your app's header height. Defaulting to 1.5rem (top-6)
  const stickyTopOffset = 'top-6'; // e.g., 'top-16' if header is h-16. This should match a Tailwind spacing scale if possible.
  // Max height for the filter sidebar is set inline in the div below using md:max-h-[calc(100vh-8rem)]
  // Adjust 8rem as needed to account for app header, page title, and desired spacing.

  return (
    <div className="container mx-auto p-4 md:p-6 h-full flex flex-col max-w-7xl">
      <div className="flex-1 flex flex-col md:flex-row gap-6 md:gap-8 overflow-hidden pt-6"> {/* Added pt-6 here to give space above filters */}
        <aside className="md:w-80 lg:w-96 flex-shrink-0 relative">
          {/* Sticky and scrollable container for filters - scrolling now handled INSIDE DiaryFilterControls */}
          <div className={`md:sticky ${stickyTopOffset} h-full md:max-h-[calc(100vh-8rem)]`}>
            <DiaryFilterControls />
          </div>
        </aside>

        <main className="flex-1 min-w-0 h-full overflow-y-auto pr-3">
          {/* Sort Controls and Add Entry Button */}
          <div className="flex justify-between items-center mb-6 flex-shrink-0">
            {/* Sort Controls */}
            <div className="flex gap-2 items-center">
              <Select value={sortField} onValueChange={(value: DiarySortField) => dispatch(setDiarySortField(value))}>
                <SelectTrigger id="globalSortField" className="w-auto h-10 text-sm min-w-[150px] bg-card">
                  <SelectValue placeholder="Sort by..." />
                </SelectTrigger>
                <SelectContent>
                  {SORTABLE_FIELDS.map(field => (
                    <SelectItem key={field.value} value={field.value} className="text-sm">{field.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={() => dispatch(toggleDiarySortDirection())} className="h-10 w-10 flex-shrink-0">
                <ArrowDownUp size={16} className={sortDirection === 'desc' ? 'rotate-180 transform' : ''} />
                <span className="sr-only">Toggle Sort Direction</span>
              </Button>
            </div>
            {/* Add Entry Button */}
            <Button onClick={handleAddNewEntry}>
              <PlusCircle className="mr-2 h-5 w-5" /> Add Entry
            </Button>
          </div>

          {reviewsLoading === 'pending' && (
            <div className="text-center py-10"><p className="text-xl text-muted-foreground">Loading...</p></div>
          )}
          {reviewsLoading === 'failed' && error && (
            <div className="text-center py-10 bg-destructive/10 p-4 rounded-md">
              <AlertTriangle className="mx-auto h-12 w-12 text-destructive mb-2" />
              <p className="text-xl text-destructive">Error loading entries</p>
              <p className="text-muted-foreground">{error}</p>
            </div>
          )}
          {reviewsLoading === 'succeeded' && filteredAndSortedReviews.length === 0 && filtersAreDefault && (
            <div className="text-center py-10"><p className="text-xl text-muted-foreground">No entries yet.</p></div>
          )}
          {reviewsLoading === 'succeeded' && filteredAndSortedReviews.length === 0 && !filtersAreDefault && (
            <div className="text-center py-10"><p className="text-xl text-muted-foreground">No entries match filters.</p></div>
          )}
          {reviewsLoading === 'succeeded' && filteredAndSortedReviews.length > 0 && (
            <div className="grid grid-cols-1 xl:grid-cols-2 grid-flow-row gap-6">
              {filteredAndSortedReviews.map((entry) => (
                <DiaryReviewItem key={entry.id} review={entry} onSelectReview={handleSelectReview} />
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Dialog for diary entry - simplified to just check activeEntryId */}
      <Dialog open={!!activeEntryId} onOpenChange={(isOpen) => { if (!isOpen) handleCloseEntryPage(); }}>
        <DialogContent 
          className="sm:max-w-2xl md:max-w-3xl lg:max-w-3xl xl:max-w-4xl w-[95vw] max-h-[90vh] flex flex-col p-0"
        >
          {activeEntryId && <DiaryEntryPage entryId={activeEntryId} onClose={handleCloseEntryPage} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DiaryPage; 